import { useEffect, useState } from 'react';

import {
  ConciergeCatalogItem,
  ConciergeCatalogPage,
  getCatalogFallbackImage,
  getConciergeCatalogImageSrc,
  hasConciergeCatalogImage,
} from '@/lib/entities/concierge';

import { Button, CircleButton } from '@/lib/shared/components/Buttons';
import { H2, H3, P2, P3 } from '@/lib/shared/components/Typography';
import { ArrowIcon } from '@/lib/shared/icons';

import clsx from 'clsx';
import useEmblaCarousel from 'embla-carousel-react';
import Image from 'next/image';

import styles from './ConciergePages.module.scss';
import { openConciergeLeadModal } from './openConciergeLeadModal';

const bookLabel = {
  ru: 'Забронировать',
  en: 'Reserve',
} as const;

export const CatalogModalContent = ({
  item,
  locale,
  assetCollection,
  showGallery = true,
  layout = 'modal',
  galleryImageNames: galleryImageNamesProp,
}: {
  item: ConciergeCatalogItem;
  locale: 'ru' | 'en';
  assetCollection?: ConciergeCatalogPage['assetCollection'];
  showGallery?: boolean;
  layout?: 'modal' | 'page';
  galleryImageNames?: string[];
}) => {
  const accent = item.accent || 'ocean';
  const modalContent = item.modal;
  const sourceForm = `concierge_${assetCollection || 'catalog'}_${item.slug}`;
  const handleCtaClick = () =>
    openConciergeLeadModal({ sourceForm, comment: item.title });

  if (modalContent) {
    const galleryImageNames =
      galleryImageNamesProp ?? modalContent.galleryImageNames ?? [];

    const galleryEntries = galleryImageNames.map((imageName) => {
      if (assetCollection && item.assetFolder) {
        const hasImage = hasConciergeCatalogImage({
          collection: assetCollection,
          assetFolder: item.assetFolder,
          imageName,
        });

        return {
          src:
            hasImage || !getCatalogFallbackImage(assetCollection)
              ? getConciergeCatalogImageSrc({
                  collection: assetCollection,
                  assetFolder: item.assetFolder,
                  imageName,
                })
              : getCatalogFallbackImage(assetCollection),
          hasImage:
            hasImage || Boolean(getCatalogFallbackImage(assetCollection)),
          placeholderLabel: `${item.assetFolder}/${imageName}`,
        };
      }

      return {
        src: '',
        hasImage: false,
        placeholderLabel: imageName,
      };
    });

    return (
      <div
        className={clsx(
          styles.modalTemplate,
          layout === 'page' && styles['modalTemplate--page']
        )}
      >
        <div className={styles.modalTemplate__copy}>
          <div className={styles.modalTemplate__copyInner}>
            {modalContent.descriptionTitle ? (
              <H3 className={styles.modalTemplate__copyTitle}>
                {modalContent.descriptionTitle}
              </H3>
            ) : null}
            {modalContent.descriptionParagraphs?.length ? (
              <div className={styles.modalTemplate__copyText}>
                {modalContent.descriptionParagraphs.map((paragraph) => (
                  <P3 key={paragraph}>{paragraph}</P3>
                ))}
              </div>
            ) : null}
            {modalContent.descriptionSections?.length ? (
              <div className={styles.modalTemplate__sections}>
                {modalContent.descriptionSections.map((section) => (
                  <section
                    className={styles.modalTemplate__section}
                    key={`${section.title}-${section.items?.length || 0}-${section.paragraphs?.length || 0}`}
                  >
                    <P2 className={styles.modalTemplate__sectionTitle}>
                      {section.title}
                    </P2>
                    {section.paragraphs?.length ? (
                      <div className={styles.modalTemplate__sectionParagraphs}>
                        {section.paragraphs.map((paragraph) => (
                          <P3 key={paragraph}>{paragraph}</P3>
                        ))}
                      </div>
                    ) : null}
                    {section.items?.length ? (
                      <div className={styles.modalTemplate__sectionList}>
                        {section.items.map((itemText) => (
                          <P3
                            className={styles.modalTemplate__sectionItem}
                            key={itemText}
                          >
                            {itemText}
                          </P3>
                        ))}
                      </div>
                    ) : null}
                  </section>
                ))}
              </div>
            ) : null}
          </div>
          <Button
            as="button"
            type="button"
            label={modalContent.ctaLabel || bookLabel[locale]}
            size="md"
            theme="dark"
            className={styles.modalTemplate__button}
            onClick={handleCtaClick}
          />
        </div>
        <div className={styles.modalTemplate__aside}>
          {showGallery && galleryEntries.length ? (
            <CatalogModalGallerySlider
              entries={galleryEntries}
              alt={item.title}
            />
          ) : null}
          {modalContent.pricingTable ? (
            <div className={styles.modalTemplate__tableCard}>
              <div className={styles.modalTemplate__tableHead}>
                <H3 className={styles.modalTemplate__tableTitle}>
                  {modalContent.pricingTable.title ||
                    (locale === 'ru' ? 'Стоимость:' : 'Rates:')}
                </H3>
                <div
                  className={styles.modalTemplate__tableColumns}
                  style={{
                    gridTemplateColumns: `repeat(${modalContent.pricingTable.columns.length}, minmax(0, 1fr))`,
                  }}
                >
                  {modalContent.pricingTable.columns.map((column) => (
                    <P2
                      className={styles.modalTemplate__tableColumn}
                      key={column}
                    >
                      {column}
                    </P2>
                  ))}
                </div>
              </div>
              <div className={styles.modalTemplate__tableRows}>
                {modalContent.pricingTable.rows.map((row) => (
                  <div
                    className={styles.modalTemplate__tableRow}
                    key={row.label}
                  >
                    <P2 className={styles.modalTemplate__tableLabel}>
                      {row.label}
                    </P2>
                    <div
                      className={styles.modalTemplate__tableValues}
                      style={{
                        gridTemplateColumns: `repeat(${row.values.length}, minmax(0, 1fr))`,
                      }}
                    >
                      {row.values.map((value, index) => (
                        <P2
                          className={styles.modalTemplate__tableValue}
                          key={`${row.label}-${index}`}
                        >
                          {value}
                        </P2>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.modalBody}>
      <div className={styles.modalCopy}>
        <H3>{item.subtitle}</H3>
        <P3>{item.summary}</P3>
        <div className={styles.modalList}>
          {item.bullets.map((bullet) => (
            <P3 key={bullet}>{bullet}</P3>
          ))}
        </div>
        <Button
          as="button"
          type="button"
          label={bookLabel[locale]}
          size="md"
          theme="dark"
          onClick={handleCtaClick}
        />
      </div>
      <div className={styles.modalAside}>
        <div
          className={clsx(styles.modalVisual, styles[`modalVisual--${accent}`])}
        >
          <div className={styles.modalVisual__glow} />
          <H2 as="h3">{item.title}</H2>
        </div>
      </div>
    </div>
  );
};

const CatalogModalGallerySlider = ({
  entries,
  alt,
}: {
  entries: Array<{
    src: string;
    hasImage: boolean;
    placeholderLabel: string;
  }>;
  alt: string;
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: entries.length > 1 });
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(entries.length > 1);

  useEffect(() => {
    if (!emblaApi) return;

    const updateState = () => {
      setCanScrollPrev(emblaApi.canScrollPrev());
      setCanScrollNext(emblaApi.canScrollNext());
    };

    updateState();
    emblaApi.on('select', updateState);
    emblaApi.on('reInit', updateState);

    return () => {
      emblaApi.off('select', updateState);
      emblaApi.off('reInit', updateState);
    };
  }, [emblaApi]);

  return (
    <div className={styles.catalogModalGallery}>
      <div className={styles.catalogModalGallery__viewport} ref={emblaRef}>
        <div className={styles.catalogModalGallery__container}>
          {entries.map((entry, index) => (
            <div
              className={styles.catalogModalGallery__slide}
              key={`${entry.placeholderLabel}-${index}`}
            >
              <div className={styles.modalTemplate__media}>
                {entry.hasImage ? (
                  <Image
                    src={entry.src}
                    alt={entries.length > 1 ? `${alt} — ${index + 1}` : alt}
                    fill
                    sizes="(min-width: 996px) 737px, 100vw"
                    loader={({ src }) => src}
                    className={styles.modalTemplate__image}
                  />
                ) : (
                  <div className={styles.modalTemplate__assetPlaceholder}>
                    {entry.placeholderLabel}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      {entries.length > 1 ? (
        <div className={styles.catalogModalGallery__controls}>
          <CircleButton
            as="button"
            type="button"
            theme="dark"
            name="Previous slide"
            className={styles.catalogModalGallery__control}
            content={<ArrowIcon />}
            disabled={!canScrollPrev}
            onClick={() => emblaApi?.scrollPrev()}
          />
          <CircleButton
            as="button"
            type="button"
            theme="dark"
            name="Next slide"
            className={styles.catalogModalGallery__control}
            content={<ArrowIcon isRight />}
            disabled={!canScrollNext}
            onClick={() => emblaApi?.scrollNext()}
          />
        </div>
      ) : null}
    </div>
  );
};
